import { cloneDeep, get } from 'lodash';

import {
  applyRegistryToGroups,
  mergeRegistryPayload,
  placeRegistryOnGroupSystem,
  readGroupSystemRegistry,
  readRegistryPayloadFromValues,
} from '@/delivery-type-plugin/capabilities/image';
import {
  MaasGroupRoleType,
  type MaasGroupedWorkloadSpec,
  MaasRunMode,
  type MaasRuntimeType,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { resolveCommonEnvFromValues } from '@/delivery-type-plugin/capabilities/maas-init/utils/commonEnv';
import { resolveModelInstancePdSeparation } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import type {
  MaasInferenceSpec,
  MaasInferenceTopologySpec,
  ModelInstanceFormValues,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { MODEL_DATA_VOLUME_NAME } from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { attachPrimaryDataVolumeForSubmit } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { assembleGroupSpecsFromNodeFields } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  DECODE_MAAS_NODE_GROUP_FIELD_PATHS,
  DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS,
  type MaasNodeGroupFieldPaths,
  PREFILL_MAAS_NODE_GROUP_FIELD_PATHS,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupFieldPaths';
import {
  ensureFrameworkForSubmit,
  normalizeMaasGroupResourceForApi,
  normalizeMaasGroupSpec,
  resolveGroupSpecsForSubmit,
  serializeMaasGroupForApi,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { resolveApiRunModeForSubmit } from '@/delivery-type-plugin/capabilities/maas-node-groups/submitValidation';
import { stripContainerDeployConfigFromGroupedSpec } from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import { parseTaskTemplateField } from '@/delivery-type-plugin/capabilities/maas-task-template';

import { KV_OFFLOAD_NODE_GROUP_FIELD_PATHS } from '../kvOffloadFieldPaths';
import { isMiKvOffloadModuleEnabled } from '../kvOffloadModule';

export interface BuildInferencePayloadOptions {
  elevateSharedEnvAndService?: boolean;
  embeddedNodeConfig?: boolean;
}

function finalizeTopologyGroups(
  values: ModelInstanceFormValues,
  fieldPaths: MaasNodeGroupFieldPaths,
  options?: BuildInferencePayloadOptions,
): MaasInferenceTopologySpec & { runMode: MaasRunMode } {
  const embedded = options?.embeddedNodeConfig !== false;
  const runModeFromField = get(values, fieldPaths.runMode) as MaasRunMode | undefined;
  const assembled = ensureFrameworkForSubmit(
    assembleGroupSpecsFromNodeFields(cloneDeep(values), values as Record<string, unknown>, {
      embeddedNodeConfig: embedded,
      elevateSharedEnvAndService: options?.elevateSharedEnvAndService === true,
      fieldPaths,
    }),
  );
  const submitValues = attachPrimaryDataVolumeForSubmit(assembled);
  /** resolveGroupSpecsForSubmit 读 values.runMode；PD 分段须用 prefill/decode 各自 runMode，不能用顶层 runMode */
  if (runModeFromField) {
    submitValues.runMode = runModeFromField;
  }
  const mountPath = values.model?.mountPath;
  const runtimeType = values.runtime?.type;
  const resolved = resolveGroupSpecsForSubmit(submitValues, runtimeType, mountPath, {
    dataVolumeName: MODEL_DATA_VOLUME_NAME,
    embeddedNodeConfig: embedded,
  });

  const formRegistry = readRegistryPayloadFromValues(values as Record<string, unknown>);
  const groupRegistry = readGroupSystemRegistry(resolved.headGroupSpec) ?? readGroupSystemRegistry(resolved.workerGroupSpecs?.[0]);
  const registry = mergeRegistryPayload(formRegistry, groupRegistry);
  const headGroupSpec = resolved.headGroupSpec ? placeRegistryOnGroupSystem(resolved.headGroupSpec, registry) : undefined;
  let workerGroupSpecs = applyRegistryToGroups(resolved.workerGroupSpecs, registry);
  workerGroupSpecs = workerGroupSpecs?.map((group) => {
    const next = normalizeMaasGroupSpec(group, MaasGroupRoleType.Worker, runtimeType);
    if (next.resource) {
      next.resource = normalizeMaasGroupResourceForApi(next.resource) || next.resource;
    }
    return placeRegistryOnGroupSystem(next, mergeRegistryPayload(registry, readGroupSystemRegistry(next)));
  });

  stripContainerDeployConfigFromGroupedSpec({ headGroupSpec, workerGroupSpecs });

  const runMode =
    runModeFromField ||
    resolveApiRunModeForSubmit({
      runMode: submitValues.runMode,
      headGroupSpec,
      workerGroupSpecs,
    });

  const topology: MaasInferenceTopologySpec = {};
  if (headGroupSpec) topology.headGroupSpec = headGroupSpec;
  if (workerGroupSpecs?.length) topology.workerGroupSpecs = workerGroupSpecs;

  return { ...attachTopologyMeta(values, fieldPaths, topology), runMode };
}

function readTopologyReplicas(values: ModelInstanceFormValues, fieldPaths: MaasNodeGroupFieldPaths): number | undefined {
  const replicas = get(values, fieldPaths.replicas);
  return typeof replicas === 'number' && replicas >= 0 ? replicas : undefined;
}

function readSegmentCommonEnv(values: ModelInstanceFormValues, commonEnvPath: string) {
  const env = resolveCommonEnvFromValues({ commonEnv: get(values, commonEnvPath) as ModelInstanceFormValues['commonEnv'] }) ?? [];
  const normalized = env.filter((item) => item?.name).map((item) => ({ name: item.name, value: item.value ?? '' }));
  return normalized.length ? normalized : undefined;
}

function attachTopologyMeta(
  values: ModelInstanceFormValues,
  fieldPaths: MaasNodeGroupFieldPaths,
  topology: MaasInferenceTopologySpec,
): MaasInferenceTopologySpec {
  const replicas = readTopologyReplicas(values, fieldPaths);
  const taskTemplate = parseTaskTemplateField(get(values, fieldPaths.taskTemplate));
  const commonEnv = readSegmentCommonEnv(values, fieldPaths.commonEnv);
  return {
    ...(replicas !== undefined ? { replicas } : {}),
    ...topology,
    ...(taskTemplate.length ? { taskTemplate } : {}),
    ...(commonEnv ? { environment: { env: commonEnv } } : {}),
  };
}

function topologyGroups(topology: MaasInferenceTopologySpec): MaasInferenceTopologySpec {
  return {
    ...(topology.headGroupSpec ? { headGroupSpec: topology.headGroupSpec } : {}),
    ...(topology.workerGroupSpecs?.length ? { workerGroupSpecs: topology.workerGroupSpecs } : {}),
    ...(typeof topology.replicas === 'number' && topology.replicas >= 0 ? { replicas: topology.replicas } : {}),
    ...(topology.taskTemplate?.length ? { taskTemplate: topology.taskTemplate } : {}),
    ...(topology.environment?.env?.length ? { environment: topology.environment } : {}),
  };
}

/** 表单 → spec.inference（不含顶层 head/worker/replicas/runMode） */
export function buildModelInstanceInferenceSpec(
  values: ModelInstanceFormValues,
  options?: BuildInferencePayloadOptions,
): { inference: MaasInferenceSpec } {
  const pdSeparation = resolveModelInstancePdSeparation(values);

  if (pdSeparation) {
    const prefill = finalizeTopologyGroups(values, PREFILL_MAAS_NODE_GROUP_FIELD_PATHS, options);
    const decode = finalizeTopologyGroups(values, DECODE_MAAS_NODE_GROUP_FIELD_PATHS, options);
    return {
      inference: {
        prefillDecode: {
          prefill: topologyGroups(prefill),
          decode: topologyGroups(decode),
        },
      },
    };
  }

  const segment = finalizeTopologyGroups(values, DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS, options);
  const inference: MaasInferenceSpec = {};
  if (segment.runMode === MaasRunMode.PrimarySecondary) {
    inference.primarySecondary = topologyGroups(segment);
  } else if (segment.workerGroupSpecs?.length) {
    inference.normal = topologyGroups(segment);
  }
  return { inference };
}

function serializeInferenceTopologyForApi(
  topology: MaasInferenceTopologySpec | undefined,
  runtimeType?: MaasRuntimeType,
): MaasInferenceTopologySpec | undefined {
  if (!topology) return undefined;
  const headGroupSpec = topology.headGroupSpec
    ? (serializeMaasGroupForApi(
        topology.headGroupSpec,
        MaasGroupRoleType.Master,
        runtimeType,
      ) as unknown as MaasInferenceTopologySpec['headGroupSpec'])
    : undefined;
  const workerGroupSpecs = topology.workerGroupSpecs?.length
    ? (topology.workerGroupSpecs.map((group) =>
        serializeMaasGroupForApi(group, MaasGroupRoleType.Worker, runtimeType),
      ) as unknown as MaasInferenceTopologySpec['workerGroupSpecs'])
    : undefined;
  if (!headGroupSpec && !workerGroupSpecs?.length) return undefined;
  return {
    ...(typeof topology.replicas === 'number' && topology.replicas >= 0 ? { replicas: topology.replicas } : {}),
    ...(headGroupSpec ? { headGroupSpec } : {}),
    ...(workerGroupSpecs?.length ? { workerGroupSpecs } : {}),
    ...(topology.taskTemplate?.length ? { taskTemplate: topology.taskTemplate } : {}),
    ...(topology.environment?.env?.length ? { environment: topology.environment } : {}),
  };
}

/** 勾选缓存节点时组装 spec.kvOffload；主从看 headGroupSpec，普通部署不写 head */
export function buildModelInstanceKvOffloadSpec(
  values: ModelInstanceFormValues,
  options?: BuildInferencePayloadOptions,
): MaasInferenceTopologySpec | undefined {
  if (!isMiKvOffloadModuleEnabled(values)) return undefined;
  const segment = finalizeTopologyGroups(values, KV_OFFLOAD_NODE_GROUP_FIELD_PATHS, options);
  const groups = topologyGroups(segment);
  const primarySecondary = segment.runMode === MaasRunMode.PrimarySecondary;
  const body: MaasInferenceTopologySpec = {
    ...(typeof groups.replicas === 'number' ? { replicas: groups.replicas } : {}),
    ...(primarySecondary && groups.headGroupSpec ? { headGroupSpec: groups.headGroupSpec } : {}),
    ...(groups.workerGroupSpecs?.length ? { workerGroupSpecs: groups.workerGroupSpecs } : {}),
    ...(groups.taskTemplate?.length ? { taskTemplate: groups.taskTemplate } : {}),
    ...(groups.environment?.env?.length ? { environment: groups.environment } : {}),
  };
  if (!body.headGroupSpec && !body.workerGroupSpecs?.length) return undefined;
  return body;
}

/** 序列化 spec.kvOffload（与推理拓扑组 spec 同一套 API 字段，不含拓扑级 initCommand） */
export function serializeMaasKvOffloadForApi(
  kvOffload: MaasInferenceTopologySpec | undefined,
  runtimeType?: MaasRuntimeType,
): MaasInferenceTopologySpec | undefined {
  return serializeInferenceTopologyForApi(kvOffload, runtimeType);
}

/** 序列化 spec.inference 为模型实例 REST 请求体 */
export function serializeMaasInferenceSpecForApi(inference: MaasInferenceSpec, runtimeType?: MaasRuntimeType): MaasInferenceSpec {
  const body: MaasInferenceSpec = {};
  const normal = serializeInferenceTopologyForApi(inference.normal, runtimeType);
  if (normal) {
    body.normal = normal;
  }
  const primarySecondary = serializeInferenceTopologyForApi(inference.primarySecondary, runtimeType);
  if (primarySecondary) {
    body.primarySecondary = primarySecondary;
  }
  const pd = inference.prefillDecode;
  if (pd?.prefill || pd?.decode) {
    body.prefillDecode = {
      ...(pd.prefill ? { prefill: serializeInferenceTopologyForApi(pd.prefill, runtimeType) } : {}),
      ...(pd.decode ? { decode: serializeInferenceTopologyForApi(pd.decode, runtimeType) } : {}),
    };
  }
  return body;
}

/** 将 inference 拓扑摊平为顶层组字段（训练集群 flatGroups 等） */
export function flattenInferenceToGroupedSpec(
  spec: Pick<ModelInstanceFormValues, never> & { inference?: MaasInferenceSpec },
): Pick<MaasGroupedWorkloadSpec, 'headGroupSpec' | 'workerGroupSpecs' | 'replicas'> {
  const inference = spec.inference;
  if (!inference) return {};
  const pd = inference.prefillDecode;
  if (pd?.prefill || pd?.decode) {
    return {};
  }
  if (inference.normal) {
    return {
      replicas: inference.normal.replicas,
      workerGroupSpecs: inference.normal.workerGroupSpecs,
    };
  }
  const ps = inference.primarySecondary;
  return {
    replicas: ps?.replicas,
    headGroupSpec: ps?.headGroupSpec,
    workerGroupSpecs: ps?.workerGroupSpecs,
  };
}

/** 非 PD：从 inference 取节点拓扑（供框架/数据盘等推导） */
export function resolveModelInstanceNonPdInferenceTopology(inference?: MaasInferenceSpec): MaasInferenceTopologySpec | undefined {
  if (!inference) return undefined;
  if (inference.primarySecondary) return inference.primarySecondary;
  if (inference.normal) return inference.normal;
  return undefined;
}
